import React, {ReactNode} from 'react'
import FacilitiesRepository from './FacilitiesRepository'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import {PageFooterArea, PageFotterAreaLeft, PageMainArea} from '../shared/hoc/withAdminPageTemplate'
import {BackLink, DeleteLink} from '../../styledComponents/adminPage/Links'
import FacilityDetails, {Equipment, FacilityOption, ImageLocation, MaxReservationTimeSetting} from './FacilityDetails'
import {Nullable} from '../../app/TypeHelpers'
import {HeadingAreaMessageParams} from '../shared/pageBaseComponents/HeadingAreaMessageComponent'
import {Avatar, Box, Card, CardHeader, CardMedia, Table, TableBody, TableCell, TableRow} from '@mui/material'
import {
    InnerMiniTable,
    InnerMiniTableBody,
    InnerMiniTableCell,
    StyledTableCaption,
    TableRowName,
} from '../../styledComponents/adminPage/Table'
import {
    ACCESS_CONTROL_MANAGER,
    AVAILABLE_OR_NOT,
    CHARGE_UNIT,
    CLEANING_TIME,
    FACILITY_CATEGORY,
    HOURS_24,
    MESSAGE_TYPE,
    PUBLISHING_SETTING,
    RESERVATION_TIME_UNIT_MINUTES,
    UNIT_OF_BUSINESS_SCHEDULE,
    VALID_OR_NOT,
} from '../../CodeMaster'
import {formatToHoursAndMinutesFromDuration} from '../../formatters/formatDuration'
import {
    FlexGrowContent,
    LeftColumnFlexContainer,
    LeftRowFlexContainer,
    RowFlexContainer,
    StretchRowFlexContainer,
} from '../../styledComponents/FlexComponents'
import {formatNumberWithCommas} from '../../formatters/formatNumberWithCommas'
import {AdminDefaultMediumButton} from '../../styledComponents/adminPage/Buttons'
import {
    TimeTableGroup,
    TimeTableItem,
} from './AdminRegisterFacilityPage/BusinessScheduleSetting/BusinessScheduleItemInterfaces'
import ReservationTimeSetting from './AdminRegisterFacilityPage/BusinessScheduleSetting/ReservationTimeSetting'
import SchedulesView from './AdminRegisterFacilityPage/BusinessScheduleSetting/SchedulesView'
import TimeTableView from './AdminRegisterFacilityPage/BusinessScheduleSetting/TimeTableView'
import {
    ReservationTimeSettingMaxReservationTimeProps,
    TimeZoneSetting,
} from './AdminRegisterFacilityPage/BusinessScheduleSetting/ReservationTimeSettingMaxReservationTime'
import styled from 'styled-components'
import {Messages} from '../../Messages'
import {
    fromMomentToEndTime,
    fromMomentToStartTime,
    fromMomentToTimeZone,
} from './AdminRegisterFacilityPage/BusinessScheduleSetting/TimeTableTimeHelper'
import {getFacilityCategoryImageUrl} from './FacilityCategoryImageHelper'
import {judgeAuthorityCheck} from '../../__NoAutomatedTests/common/util/Natutil'
import moment from 'moment'
import {Location} from 'react-router-dom'

interface Props {
    facilitiesRepo: FacilitiesRepository
    condominiumCd: string
    facilityId: number
    location: Location
}

type EditModeType = 'none' | 'facility' | 'facility-introduction'

interface State {
    facility: Nullable<FacilityDetails>
    editMode: EditModeType
}

class AdminFacilityDetailsPage extends React.Component<Props & AdminPagePropsSet, State> {

    static displayName: string = 'AdminFacilityDetailsPage'

    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)
        this.state = {
            facility: null,
            editMode: 'none',
        }

        this.handleEditFacilityButtonClick = this.handleEditFacilityButtonClick.bind(this)
        this.handleEditFacilityIntroductionButtonClick = this.handleEditFacilityIntroductionButtonClick.bind(this)
        this.handleDeleteButtonClick = this.handleDeleteButtonClick.bind(this)
        this.handleDeleteCancelButtonClick = this.handleDeleteCancelButtonClick.bind(this)
        this.handleDeleteOkButtonClick = this.handleDeleteOkButtonClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            let editMode: EditModeType
            if (judgeAuthorityCheck('EDIT-EQUIPMENT_FACILITY')) {
                editMode = 'facility'
            } else if (judgeAuthorityCheck('EDIT-FACILITY_DESCRIPTION')) {
                editMode = 'facility-introduction'
            } else {
                editMode = 'none'
            }
            const {facilitiesRepo, condominiumCd, facilityId} = this.props

            const facility: Nullable<FacilityDetails> = await this.props.asyncCallFunction(
                facilitiesRepo.getFacilityDetails.bind(facilitiesRepo, condominiumCd, facilityId),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )
            if (facility) {
                this.setState({
                    facility: facility,
                    editMode: editMode,
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }

        this.showInitialMessage()
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

    handleEditFacilityButtonClick(): void {
        const {condominiumCd, facilityId} = this.props
        this.props.navigate(`/admin/condominiums/${condominiumCd}/facilities/${facilityId}/edit`)
    }

    handleEditFacilityIntroductionButtonClick(): void {
        const {condominiumCd, facilityId} = this.props
        this.props.navigate(
            `/admin/condominiums/${condominiumCd}/facilities/${facilityId}/introductoryEdit`,
            {
                state: {
                    previousPageUrl: this.props.location.pathname,
                    previousPageName: '施設詳細',
                },
            },
        )
    }

    handleDeleteButtonClick(): void {
        if (this.state.facility === null) {
            return
        }
        this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IOPCC0011.errorLevel,
                message: Messages.IOPCC0011.toString(this.state.facility.facilityName),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleDeleteCancelButtonClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleDeleteOkButtonClick,
                    },
                ],
            },
        )
    }

    handleDeleteCancelButtonClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleDeleteOkButtonClick(): Promise<void> {
        this.props.eventEmitter.closeDialog()

        const {facilitiesRepo, condominiumCd, facilityId} = this.props

        let succeed: Nullable<boolean> = false
        await this.props.eventEmitter.showLoading()
        try {
            succeed = await this.props.asyncCallFunction(
                facilitiesRepo.deleteFacility.bind(facilitiesRepo, condominiumCd, facilityId),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
        if (!succeed) {
            return
        }

        const facilityName = this.state.facility ? this.state.facility.facilityName : ''
        this.props.navigate(
            `/admin/condominiums/${condominiumCd}/facilities/search`,
            {
                state: {
                    initialMessage: {
                        title: null,
                        messageLevel: MESSAGE_TYPE.INFORMATION,
                        detail: Messages.IOPCC0006.toString('施設', facilityName),
                    },
                },
            },
        )
    }

    renderPageActionArea(): ReactNode {
        if (this.state.facility === null) {
            return
        }

        if (this.state.editMode === 'facility') {
            return (<>
                <Box marginTop={'10px'}>
                    <RowFlexContainer>
                        <AdminDefaultMediumButton onClick={this.handleEditFacilityButtonClick}>
                            施設編集
                        </AdminDefaultMediumButton>
                        <DeleteLink onClick={this.handleDeleteButtonClick}>施設を削除する</DeleteLink>
                    </RowFlexContainer>
                </Box>
            </>)
        } else if (this.state.editMode === 'facility-introduction') {
            return (<>
                <Box marginTop={'10px'}>
                    <RowFlexContainer>
                        <AdminDefaultMediumButton onClick={this.handleEditFacilityIntroductionButtonClick}>
                            施設紹介文編集
                        </AdminDefaultMediumButton>
                    </RowFlexContainer>
                </Box>
            </>)
        }
    }

    renderEquipmentsTable(equipments: Equipment[]): ReactNode {
        return (
            <InnerMiniTable>
                <InnerMiniTableBody>
                    {
                        equipments.map((equipment, index) => {
                            return (
                                <TableRow key={index}>
                                    <InnerMiniTableCell>{index + 1}</InnerMiniTableCell>
                                    <InnerMiniTableCell>
                                        {equipment.name}
                                    </InnerMiniTableCell>
                                </TableRow>
                            )
                        })
                    }
                </InnerMiniTableBody>
            </InnerMiniTable>
        )
    }

    renderImageCard(images: ImageLocation[], category: FACILITY_CATEGORY): ReactNode {
        if (images.length === 0) {
            return <Card>
                <StyledCardContent>
                    <CardMedia
                        style={{maxWidth: '200px', maxHeight: '200px'}}
                        component={'img'}
                        src={getFacilityCategoryImageUrl(category)}
                    />
                </StyledCardContent>
            </Card>
        }

        return (<>{
            images.map((image, index) => {
                return (
                    <Box
                        ml={(index !== 0) ? '10px' : undefined}
                        key={index}
                    >
                        <Card>
                            <StyledCardContent>
                                <CardMedia
                                    style={{maxWidth: '200px', maxHeight: '200px'}}
                                    component={'img'}
                                    src={image.url}
                                />
                            </StyledCardContent>
                        </Card>
                    </Box>
                )
            })
        }</>)
    }

    renderOptionCard(option: FacilityOption, index: number): ReactNode {
        const quantityRange: string =
            (option.volumeMin !== option.volumeMax)
                ? `${option.volumeMin}〜${option.volumeMax}${option.unitName}`
                : `${option.volumeMin}${option.unitName}`
        const requiredString: string = (option.appointRequired === VALID_OR_NOT.VALID) ? '必須' : '任意'
        const chargeString: string = this.createChargeString(option)
        const paymentType: string = option.charge === 0 ? '' : option.paymentMethod.label

        return (
            <FacilityOptionCard key={index}>
                <Card square>
                    <CardHeader
                        style={{padding: '8px'}}
                        avatar={
                            <Avatar>{option.sortOrder}</Avatar>
                        }
                        title={option.name}
                        subheader={
                            <LeftColumnFlexContainer>
                                <div>{quantityRange}（{requiredString}）</div>
                                <div>{chargeString}</div>
                                <div>{paymentType}</div>
                            </LeftColumnFlexContainer>
                        }
                    />
                </Card>
            </FacilityOptionCard>
        )
    }

    createChargeString(option: FacilityOption): string {
        if (option.charge === 0) {
            return '無料'
        }
        const chargeWithUnit: string = `${formatNumberWithCommas(option.charge)}円`
        if (option.chargeUnit === CHARGE_UNIT.FIXED) {
            return `${chargeWithUnit}/固定`
        } else {
            return `${chargeWithUnit}/${option.chargeUnitTime}${option.chargeUnit.label}`
        }
    }

    renderDetailsTable(): ReactNode {
        const {
            facility,
        } = this.state
        if (facility === null) {
            return
        }
        return (<>
            <Box marginTop={'10px'}>
                {this.renderFacilityInfoTable(facility)}
                {this.renderStaySettingTable(facility)}
                {this.renderBusinessSchedules(facility)}
            </Box>
        </>)
    }

    renderFacilityInfoTable(facility: FacilityDetails): ReactNode {
        let reservationStartText: string
        const {reserveReceptionStartMonth, reserveReceptionStartDate} = facility.reservationSetting
        if (reserveReceptionStartMonth !== null) {
            reservationStartText = `${reserveReceptionStartMonth}ヶ月前`
        } else if (reserveReceptionStartDate !== null) {
            reservationStartText = `${reserveReceptionStartDate}日前`
        } else {
            reservationStartText = '制限なし'
        }

        let reservationLimitText: string
        const {reserveReceptionLimitDate, reserveReceptionLimitTime} = facility.reservationSetting
        if (reserveReceptionLimitDate !== null) {
            reservationLimitText = `（当日除く）${reserveReceptionLimitDate}日前まで`
        } else if (reserveReceptionLimitTime !== null) {
            reservationLimitText = `当日${formatToHoursAndMinutesFromDuration(reserveReceptionLimitTime)}前まで`
        } else {
            reservationLimitText = '制限なし'
        }

        let cancelSettingText: string
        const {cancelAvailability, cancelReceptionLimitDate, cancelReceptionLimitTime} = facility.reservationSetting
        cancelSettingText = cancelAvailability.label
        if (cancelAvailability === AVAILABLE_OR_NOT.AVAILABLE) {
            let cancelSettingSubText: string
            if (cancelReceptionLimitDate !== null) {
                cancelSettingSubText = `（当日除く）${cancelReceptionLimitDate}日前まで`
            } else if (cancelReceptionLimitTime !== null) {
                cancelSettingSubText = `当日${formatToHoursAndMinutesFromDuration(cancelReceptionLimitTime)}前まで`
            } else {
                cancelSettingSubText = '制限なし'
            }
            cancelSettingText += `（キャンセル可能期間：${cancelSettingSubText}）`
        }

        let releaseStartDateTime: string = ''
        if (facility.releaseSetting.value === PUBLISHING_SETTING.PRIVATE.value) {
            if (facility.releaseStartDate !== null && facility.releaseStartTime !== null) {
                const startDateString = moment(facility.releaseStartDate + 'T00:00:00Z').format('YYYY/M/D')
                releaseStartDateTime = '公開開始日時：' + startDateString + ' ' + facility.releaseStartTime.value + '時'
            }
        }

        return (<>
            <Table>
                <StyledTableCaption>施設情報</StyledTableCaption>
                <TableBody>
                    <TableRow>
                        <TableRowName>並び順</TableRowName>
                        <TableCell>
                            {facility.sortOrder}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>カテゴリ</TableRowName>
                        <TableCell>
                            {facility.category.label}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>設備</TableRowName>
                        <TableCell>
                            {this.renderEquipmentsTable(facility.equipments)}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>施設名</TableRowName>
                        <TableCell>
                            {facility.facilityName}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>入退室管理</TableRowName>
                        <TableCell>
                            <LeftRowFlexContainer>
                                {facility.accessControlManager?.label ?? 'なし'}
                                {facility.accessControlManager === ACCESS_CONTROL_MANAGER.ODEC_TECHNO && <>
                                    <Box width={'10px'}/>
                                    {`（カードキー施設番号：${facility.cardKeyFacilityNumber}）`}
                                </>}
                                {facility.accessControlManager === ACCESS_CONTROL_MANAGER.TEBRA_PASS && <>
                                    <Box width={'10px'}/>
                                    {`（ゲート番号：${facility.gateNumbers.join('，')}）`}
                                </>}
                            </LeftRowFlexContainer>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>施設紹介文</TableRowName>
                        <TableCell>
                            <FacilityIntroductory
                                dangerouslySetInnerHTML={{__html: facility.facilityIntroductory || ''}}/>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>画像</TableRowName>
                        <TableCell>
                            <LeftRowFlexContainer>
                                {this.renderImageCard(facility.images, facility.category)}
                            </LeftRowFlexContainer>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>公開状態</TableRowName>
                        <TableCell>
                            <LeftRowFlexContainer>
                                {facility.releaseSetting.label}
                                <Box marginRight={'30px'}/>
                                {releaseStartDateTime}
                            </LeftRowFlexContainer>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            <Table>
                <StyledTableCaption>予約設定</StyledTableCaption>
                <TableBody>
                    <TableRow>
                        <TableRowName>居住者予約</TableRowName>
                        <TableCell>
                            {facility.reservationSetting.residentReserve.label}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>抽選設定</TableRowName>
                        <TableCell>
                            {facility.reservationSetting.lotteryAvailability.label}
                            {
                                facility.reservationSetting.lotteryAvailability === AVAILABLE_OR_NOT.AVAILABLE &&
                                `（優先度：${facility.reservationSetting.lotteryPriority}）`
                            }
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>予約受付開始日</TableRowName>
                        <TableCell>
                            {reservationStartText}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>予約受付締切日</TableRowName>
                        <TableCell>
                            {reservationLimitText}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>キャンセル設定</TableRowName>
                        <TableCell>
                            {cancelSettingText}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>貸出方法</TableRowName>
                        <TableCell>
                            {facility.staySetting !== null && '宿泊'}
                            {facility.staySetting === null && '時間貸し'}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </>)
    }

    renderStaySettingTable(facility: FacilityDetails): ReactNode {
        if (facility.staySetting === null) {
            return null
        }

        return (<>
            <Table>
                <StyledTableCaption>宿泊施設の設定</StyledTableCaption>
                <TableBody>
                    <TableRow>
                        <TableRowName>オプション設定</TableRowName>
                        <TableCell>
                            {[...facility.optionSettings]
                                .sort((prev, next) => prev.sortOrder - next.sortOrder)
                                .map((option, index) => this.renderOptionCard(option, index))}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>宿泊設定</TableRowName>
                        <TableCell>
                            <LeftRowFlexContainer>
                                <Box marginRight={'10px'}>宿泊可能日数</Box>
                                <Box marginRight={'30px'}>{facility.staySetting.stayPossibleDateCount}日まで</Box>
                                <Box marginRight={'10px'}>連泊中時間貸</Box>
                                <Box>{facility.staySetting.stayContinueTimeRental.label}</Box>
                            </LeftRowFlexContainer>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>チェックインアウト</TableRowName>
                        <TableCell>
                            <LeftRowFlexContainer>
                                <Box marginRight={'10px'}>{facility.staySetting.checkinTime.format('H時 mm分')}</Box>
                                <Box marginRight={'10px'}>〜</Box>
                                <Box>{facility.staySetting.checkoutTime.format('H時 mm分')}</Box>
                            </LeftRowFlexContainer>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>掃除の時間</TableRowName>
                        <TableCell>
                            <LeftRowFlexContainer>
                                <Box marginRight={'10px'}>予約前</Box>
                                <Box marginRight={'20px'}>{facility.reserveBeforeCleaningTime}分</Box>
                                <Box marginRight={'10px'}>予約後</Box>
                                <Box>{facility.reserveAfterCleaningTime}分</Box>
                            </LeftRowFlexContainer>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>料金</TableRowName>
                        <TableCell>
                            {formatNumberWithCommas(facility.staySetting.stayCharge)}円
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </>)
    }

    renderBusinessSchedules(facility: FacilityDetails): ReactNode {
        if (facility.staySetting !== null) {
            return null
        }

        let timeTableItems: TimeTableItem[] = []
        if (facility.businessSchedules !== null) {
            timeTableItems = facility.businessSchedules.map((schedule, index) => {
                return {
                    id: `${index}`,
                    title: '営業',

                    businessScheduleUnit: schedule.businessScheduleUnit,
                    dateStraddle: schedule.dateStraddle,
                    joinPrevDaysSchedule: schedule.joinPrevDaysSchedule,

                    startTime: schedule.startTime === null ? null : fromMomentToStartTime(schedule.startTime),
                    endTime: schedule.endTime === null ? null : fromMomentToEndTime(schedule.endTime),

                    charge: schedule.charge,
                    chargeUnit: schedule.chargeUnit,
                    chargeUnitTime: schedule.chargeUnitTime,
                    changedChargeApplyStartTime: schedule.changedChargeApplyStartTime,
                    changedChargeApplyStartTimeUnit: schedule.changedChargeApplyStartTimeUnit,
                    changedCharge: schedule.changedCharge,
                    changedChargeUnit: schedule.changedChargeUnit,
                    changedChargeUnitTime: schedule.changedChargeUnitTime,
                }
            })
        }

        const cleaningTimeBeforeReservation: CLEANING_TIME =
            CLEANING_TIME.codeToEnumConstants(`${facility.reserveBeforeCleaningTime}`)
        const cleaningTimeAfterReservation: CLEANING_TIME =
            CLEANING_TIME.codeToEnumConstants(`${facility.reserveAfterCleaningTime}`)

        if (facility.maxReservationTimeSetting === null) {
            throw Error('時間貸し施設に最大予約時間の設定がない')
        }

        const isStraddleSchedule =
            (timeTableItems.length === 1
                && timeTableItems[0].businessScheduleUnit === UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE)

        const groupItems: TimeTableGroup[] =
            isStraddleSchedule ? this.createGroupItemsForStraddleSchedule() : this.createGroupItems()

        return (<>
            <Table>
                <StyledTableCaption>時間貸し施設の設定</StyledTableCaption>
                <TableBody>
                    <TableRow>
                        <TableRowName>オプション設定</TableRowName>
                        <TableCell>
                            {[...facility.optionSettings]
                                .sort((prev, next) => prev.sortOrder - next.sortOrder)
                                .map((option, index) => this.renderOptionCard(option, index))}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            <StretchRowFlexContainer>
                <ReservationTimeSetting
                    cleaningSetting={{
                        cleaningTimeBeforeReservation: cleaningTimeBeforeReservation,
                        cleaningTimeAfterReservation: cleaningTimeAfterReservation,
                    }}
                    maxReservationTimeSetting=
                        {this.convertMaxReservationTimeSetting(facility.maxReservationTimeSetting)}
                />
                <FlexGrowContent>
                    <SchedulesView
                        groupItems={groupItems}
                        items={timeTableItems}
                    />
                </FlexGrowContent>
            </StretchRowFlexContainer>
            {
                (!isStraddleSchedule) &&
                <TimeTableView
                    groupItems={groupItems}
                    defaultTimeStart={{
                        hour: HOURS_24.VOL_0,
                        minute: RESERVATION_TIME_UNIT_MINUTES.VOL_0,
                    }}
                    defaultTimeEnd={{
                        hour: HOURS_24.VOL_24,
                        minute: RESERVATION_TIME_UNIT_MINUTES.VOL_0,
                    }}
                    pixelPer5Minutes={2}
                    items={timeTableItems}
                />
            }
        </>)
    }

    convertMaxReservationTimeSetting(
        setting: MaxReservationTimeSetting,
    ): ReservationTimeSettingMaxReservationTimeProps {
        let timeZoneSettings: Nullable<TimeZoneSetting[]> = null
        if (setting.reservationTimeSections !== null) {
            timeZoneSettings = setting.reservationTimeSections.map((section, index, sections) => {
                const startTime = fromMomentToTimeZone(section.startTime)
                const endTime = fromMomentToTimeZone(sections[(index + 1) % sections.length].startTime)
                const ret: TimeZoneSetting = {
                    reservationTimeMax: section.maxReservationTime,
                    startTime: startTime,
                    endTime: endTime,
                }
                return ret
            })
        }

        return {
            allDayReservationTimeMax: setting.allDayMaxReservationTime,
            timeZoneSettings: timeZoneSettings,
        }
    }

    createGroupItems(): TimeTableGroup[] {
        return UNIT_OF_BUSINESS_SCHEDULE.values()
            .filter(groupItem => {
                return (groupItem !== UNIT_OF_BUSINESS_SCHEDULE.ALL_DAYS
                    && groupItem !== UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE)
            })
            .map(groupItem => {
                return {businessScheduleUnit: groupItem, title: groupItem.label}
            })
    }

    createGroupItemsForStraddleSchedule(): TimeTableGroup[] {
        return [{
            businessScheduleUnit: UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE,
            title: UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE.label,
        }]
    }

    render(): ReactNode {
        return (<>
            {this.renderPageActionArea()}
            <PageMainArea>
                {this.renderDetailsTable()}
            </PageMainArea>

            <PageFooterArea>
                <PageFotterAreaLeft>
                    <BackLink
                        to={`/admin/condominiums/${this.props.condominiumCd}/facilities/search`}>
                        施設検索へ戻る
                    </BackLink>
                </PageFotterAreaLeft>
            </PageFooterArea>
        </>)
    }
}

export default createAdminPage(AdminFacilityDetailsPage)

const FacilityOptionCard = styled.div`
  display: inline-block;
  margin-top: 8px;
  margin-right: 8px;
`

const StyledCardContent = styled.div`
  background-color: #EEEEEE;
  width: 150px;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
`
const FacilityIntroductory = styled.div`
  word-break: break-all;
`
